import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import objectPath from 'object-path';
import { useRouter } from 'next/router';
import {
  Button, ButtonSize, Modal, InfoBox, SpacingName, Spacing, BreakpointQueryName, useModal, Typography, Portal,
} from '@doveit/bricks';
import { pluralizeWord } from '@doveit/hammer';
import GraphImg from '@graphcms/react-image';
import { NextPage } from 'next';
import { useMediaQuery } from 'styled-breakpoints/use-media-query';
import jwt from 'jsonwebtoken';
import getConfig from 'next/config';
import { useTheme } from 'styled-components';
import LayoutWrapper from '../client/layouts/layout-wrapper/LayoutWrapper';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { getAggregationsByArea, PropertyFilters, PropertyFiltersLocation } from '../services/properties/properties';
import httpErrors from '../client/utils/error/http/httpErrors';
import {
  buildListingURLObject, buildSerpURLObject, goToPage, BuildSerpURLObject, URLObject,
} from '../client/utils/links';
import { buildBreadcrumb } from '../client/utils/breadcrumb/properties';
import { buildPropertySerpSEO, buildSERPH1 } from '../client/utils/serp/seoUtils';
import PropertiesList from '../client/components/properties-list/PropertiesList';
import LinksListSection from '../client/templates/common/links-list-section/LinksListSection';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import { SERP_CONTENTS_BY_PROVINCE_SLUG, SERP_CONTENTS_BY_LOCALITY_AND_AREA_SLUG, SERP_CONTENTS_BY_LOCALITY_SLUG } from '../client/graphql/queries/serpContents';
import Stripe, { StripeDirection } from '../client/components/stripe/Stripe';
import RapidLinks from '../client/templates/common/rapid-links/RapidLinks';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import {
  hasSerpContent, mapPropertyFilterFormModel, mapBuildSerpUrlObject, propertiesAreaToLinksList, queryToFilters,
} from '../client/utils/serp';
import { BANNER_SELL_PROPERTY, TUNE } from '../client/themes/icons';
import PropertyFilterForm, { PropertyFilterFormModel } from '../client/components/property-filter-form/PropertyFilterForm';
import { getSearchResults, SearchResults } from '../services/search/getSearchResults';
import { getMinSoldAtDate } from '../client/utils/search/filters';
import { getLogger } from '../logger/isomorphic';
import { SeoLink } from '../client/components/seo-links/SeoLinks';
import { getAdvByType } from '../services/advs/advs';
import { Adv } from '../domain/adv';
import { AdvFluidType } from '../domain/graphcms';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import FeaturedBox from '../client/components/featured-box/FeaturedBox';
import { CTA } from '../client/types';
import * as styles from '../client/templates/serp/Serp.style';
import { getWarningMessage } from '../client/utils/serp/getWarningMessage';
import initGraphQLClient, { GraphQLRequest, Variables } from '../client/graphql/initGraphQLClient';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import { SortOrder } from '../domain/pagination';
import { WebSearchCriteria } from '../services/dtos';
import { getWebSearchCriteriaChecksum } from '../client/utils/serp/getWebSearchCriteriaChecksum';
import { stringifyWebSearchCriteriaContact } from '../client/utils/serp/stringifyWebSearchCriteriaContact';
import { mapPropertyFilterFormModelToSearchCriteria } from '../client/utils/serp/mapPropertyFilterFormModelToSearchCriteria';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import { DataTrackingContext } from '../client/constants';
import StickyBar from '../client/templates/common/sticky-bar/StickyBar';
import useSessionStorage from '../client/hooks/useSessionStorage/useSessionStorage';
import CreateWebSearchCriteria from '../client/containers/create-web-search-criteria/CreateWebSearchCriteria';
import CreateWebSearchCriteriaButton from '../client/components/create-web-search-criteria-button/CreateWebSearchCriteriaButton';

export interface SerpPageProps {
  SEO: PageHeadProps['SEO'],
  query: BuildSerpURLObject,
  results: SearchResults,
  province?: PropertyFiltersLocation,
  locality?: PropertyFiltersLocation,
  area?: PropertyFiltersLocation,
  adv: Adv,
  areaLinks?: SeoLink[],
  serpData?: any,
  buyerConsultancy: {
    caption: string,
    title: string,
    description: string,
    cta: CTA & {
      urlObject: URLObject,
    },
  },
}

const INTERSECTION_OPTIONS = { threshold: 0 };

export const ENABLED_LOCALITIES_TOP_AREAS = ['milano', 'torino', 'varese', 'roma', 'taranto'];
export const SEARCH_CRITERIA_STORAGE_BASE_KEY = 'saved-search-criteria';
export const CONTACT_STORAGE_KEY = 'saved-search-criteria-contact';
export const SAVE_SEARCH_CRITERIA_MODAL_SHOWN = 'save-search-criteria-modal-shown';

export const MINIMUM_MODAL_SCROLL = 100;

const logger = getLogger();

export const SerpPage: NextPage<SerpPageProps> = ({
  SEO,
  query,
  results,
  province,
  locality,
  area,
  adv,
  areaLinks,
  serpData,
  buyerConsultancy,
}) => {
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isFilterModalLoading, setFilterModalLoading] = useState(false);

  const [isPageScrolled, setIsPageScrolled] = useState(false);

  const { ref: saveSearchRef, isVisible: isSaveButtonVisible } = useRefInView(INTERSECTION_OPTIONS);

  const onScroll = useCallback(() => {
    if (window.scrollY > MINIMUM_MODAL_SCROLL) {
      setIsPageScrolled(true);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  // Trick to close modal when the page filters change (e.g. after the search form is submitted)
  const router = useRouter();

  const initialValuesFormData = useMemo(
    () => mapPropertyFilterFormModel(results.accurate),
    [results.accurate],
  );

  const initialWebSearchCriteria: Partial<WebSearchCriteria> = useMemo(
    () => (mapPropertyFilterFormModelToSearchCriteria(initialValuesFormData)),
    [initialValuesFormData],
  );

  const [searchAlreadySaved, setSearchAlreadySaved] = useState(false);
  const [initialWebSearchCriteriaWithContact, setInitialWebSearchCriteriaWithContact] = useState(initialWebSearchCriteria);
  const [currentWebSearchCriteria, setCurrentWebSearchCriteria] = useState(initialWebSearchCriteria);
  const [isSaveSearchModalShown, setIsSaveSearchModalShown] = useSessionStorage(
    `${SAVE_SEARCH_CRITERIA_MODAL_SHOWN}-${getWebSearchCriteriaChecksum(currentWebSearchCriteria)}`,
    false,
  );

  useEffect(() => {
    const contact = window.localStorage.getItem(CONTACT_STORAGE_KEY);
    if (contact) {
      setInitialWebSearchCriteriaWithContact({
        ...currentWebSearchCriteria,
        ...JSON.parse(contact),
      });
    } else {
      setInitialWebSearchCriteriaWithContact(currentWebSearchCriteria);
    }

    if (currentWebSearchCriteria.place !== undefined) {
      const searchCriteriaHash = getWebSearchCriteriaChecksum(currentWebSearchCriteria);
      const alreadySaved = window.localStorage.getItem(`${SEARCH_CRITERIA_STORAGE_BASE_KEY}:${searchCriteriaHash}`) !== null;
      setSearchAlreadySaved(alreadySaved);
    }
  }, [currentWebSearchCriteria]);

  const getSerpUrl = useCallback(
    (page) => buildSerpURLObject({ ...query, p: page }),
    [query],
  );

  const handleRouteChange = useCallback(() => {
    setFilterModalLoading(false);
    setFilterModalOpen(false);
  }, []);

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, handleRouteChange]);

  const onPageChange = useCallback(
    (page) => goToPage(getSerpUrl(page)),
    [getSerpUrl],
  );

  const onSaveSearchCriteria = useCallback((searchCriteria: WebSearchCriteria) => {
    setCurrentWebSearchCriteria(searchCriteria);
    if (window !== undefined) {
      window.localStorage.setItem(`${SEARCH_CRITERIA_STORAGE_BASE_KEY}:${getWebSearchCriteriaChecksum(searchCriteria)}`, 'true');
      window.localStorage.setItem(CONTACT_STORAGE_KEY, stringifyWebSearchCriteriaContact(searchCriteria));
    }
  }, []);

  const onPropertyClick = useCallback((property) => {
    goToPage(buildListingURLObject(property));
  }, []);

  const buildPagePath = useCallback(
    (page) => getSerpUrl(page).as,
    [getSerpUrl],
  );

  const isGeoSearch = useMemo(
    () => !!(province || locality || area),
    [area, locality, province],
  );

  const breadcrumb = useMemo(
    () => buildBreadcrumb({
      province: locality ? serpData?.locality?.province?.value : province?.name,
      locality: locality?.name,
      area: area?.name,
    }),
    [area?.name, locality, province?.name, serpData?.locality?.province],
  );

  const warningMessage = useMemo(() => getWarningMessage(results), [results]);
  const otherResults = useMemo(
    () => [].concat(results?.nearby?.content ?? [], results?.similar?.content ?? []).filter((i) => i),
    [results],
  );

  const structuredData = useMemo(
    () => getStructuredData(getBreadcrumbStructuredData(breadcrumb)),
    [breadcrumb],
  );

  const saveSearchModal = useModal();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(BreakpointQueryName.MD));

  const isFirstPage = useMemo(() => (query.p === null || query.p === '1'), [query.p]);

  useEffect(() => {
    if (!isSaveButtonVisible
      && isPageScrolled
      && isGeoSearch
      && !searchAlreadySaved
      && !isSaveSearchModalShown
      && !isMobile
      && isFirstPage) {
      setIsSaveSearchModalShown(true);
      saveSearchModal.open();
    }
  }, [
    isGeoSearch,
    isMobile,
    isPageScrolled,
    isSaveSearchModalShown,
    isSaveButtonVisible,
    searchAlreadySaved,
    saveSearchModal,
    isFirstPage,
    setIsSaveSearchModalShown]);

  const onFilterClose = useCallback((propertyFilterFormModel: PropertyFilterFormModel) => {
    setCurrentWebSearchCriteria(mapPropertyFilterFormModelToSearchCriteria(propertyFilterFormModel));
    setFilterModalLoading(true);
    setIsPageScrolled(false);
    goToPage(buildSerpURLObject(mapBuildSerpUrlObject(propertyFilterFormModel)));
  }, []);

  const closePopupOnSaveSearchCriteria = useCallback((webSearchCriteria: WebSearchCriteria) => {
    onSaveSearchCriteria(webSearchCriteria);
    saveSearchModal.close();
  }, [onSaveSearchCriteria, saveSearchModal]);

  return (
    <AppLayout activePage="property-for-sale">
      <PageHead
        SEO={SEO}
        structuredData={structuredData}
      />

      <LayoutWrapper>
        <Spacing padding={[SpacingName.BIG, SpacingName.ZERO]}>
          <header css={styles.header}>
            <div>
              <h1
                css={styles.title}
                data-ref="SERP-title"
              >
                {buildSERPH1(province, locality, area)}
              </h1>
              <p css={styles.resultsCount}>
                <span data-ref="search-result-count">
                  {results.accurate.totalElements}
                </span>
                {' '}
                {pluralizeWord('risultat', 'o', 'i', results.accurate.totalElements)}
              </p>
            </div>
            <div css={styles.search}>
              {isGeoSearch && (
                <div ref={saveSearchRef}>
                  <CreateWebSearchCriteriaButton
                    dataRef="save-search-main"
                    aria-label="save-search-main"
                    dataTracking={`save-search:${DataTrackingContext.MAIN}`}
                    onSuccess={onSaveSearchCriteria}
                    initialValues={initialWebSearchCriteriaWithContact}
                    disabled={searchAlreadySaved}
                  />
                </div>
              )}
              <Button
                icon={TUNE}
                label="Filtra"
                aria-label="Filtra immobili"
                size={ButtonSize.SMALL}
                dataRef="filter-button"
                onClick={() => setFilterModalOpen(true)}
              />
              <Modal
                dataRef="filter-button-modal"
                open={isFilterModalOpen}
                onCloseHandler={() => setFilterModalOpen(false)}
              >
                <Modal.Close />
                <Modal.Header title="Filtra i risultati di ricerca" />
                <Modal.Body padded>
                  <PropertyFilterForm
                    onSubmit={onFilterClose}
                    loading={isFilterModalLoading}
                    initialValues={initialValuesFormData}
                  />
                </Modal.Body>
              </Modal>
            </div>
          </header>

          {results.accurate.numberOfElements > 0 && (
            <PropertiesList
              aria-label="properties-list"
              properties={results.accurate.content}
              onItemClick={onPropertyClick}
              pagination={{
                ...results.accurate.pageable,
                totalElements: results.accurate.totalElements,
                totalPages: results.accurate.totalPages,
                onPageChange,
                buildPagePath,
              }}
              render={{
                inPosition: (index) => (index === 2 && (
                  <div css={styles.itemAdv}>
                    <div css={styles.advWrapper}>
                      <AdvFluid
                        title={adv.title}
                        description={adv.description}
                        cta={adv.cta}
                        urlObject={adv.urlObject}
                        symbolPath={BANNER_SELL_PROPERTY}
                        dataRef="adv-fluid-v1-main-sell-properties-serp"
                        dataTracking="adv-fluid-v1-main-sell-properties-serp"
                      />
                    </div>
                  </div>
                )),
              }}
            />
          )}

          {warningMessage && (
            <div aria-label="warning-box" css={styles.warningMessage}>
              <InfoBox message={warningMessage} />
            </div>
          )}

          {otherResults.length > 0 && (
            <PropertiesList
              aria-label="serp-other-results"
              data-ref="serp-other-results"
              properties={otherResults}
              onItemClick={onPropertyClick}
            />
          )}
        </Spacing>

        <Spacing padding={[0, 0, SpacingName.BIG]}>
          <FeaturedBox
            caption={buyerConsultancy.caption}
            title={buyerConsultancy.title}
            description={buyerConsultancy.description}
            cta={{
              ...buyerConsultancy.cta,
              href: buyerConsultancy.cta.urlObject.as,
              onClick: () => goToPage(buyerConsultancy.cta.urlObject),
            }}
          />
        </Spacing>
      </LayoutWrapper>

      {areaLinks && areaLinks.length > 0 && (
        <>
          <SectionDivider />
          <LayoutWrapper>
            <LinksListSection
              aria-label="links-list-section"
              title={`Scopri le aree di ${locality.name}`}
              links={areaLinks}
              onLinkClick={(to) => goToPage(to)}
            />
          </LayoutWrapper>
        </>
      )}

      {hasSerpContent(serpData) && serpData.serpContents[0].blocks.length > 0 && (
        <div css={styles.serpContents}>
          <LayoutWrapper>
            {serpData.serpContents[0].blocks.map((block) => (
              <div
                css={styles.serpContentBlock}
                key={block.title}
              >
                <Stripe
                  title={block.title}
                  description={block.content}
                  direction={StripeDirection.RIGHT_TO_LEFT}
                  imageSlot={block.imageBlock ? (
                    <GraphImg
                      css={styles.serpContentImage}
                      image={{
                        handle: block.imageBlock.image.handle,
                        width: 340,
                        height: 224,
                      }}
                      maxWidth={340}
                      alt={block.imageBlock.imageAlt}
                    />
                  ) : null}
                />
              </div>
            ))}
          </LayoutWrapper>
        </div>
      )}

      {serpData && serpData.province && serpData.province.capital && (
        <Spacing margin={[SpacingName.MEDIUM, 0]}>
          <RapidLinks
            aria-label="rapid-links-province"
            locality={serpData.province.capital}
            showAgency={hasSerpContent(serpData) && serpData.serpContents[0].hasAgency}
            showEvaluation={hasSerpContent(serpData) && serpData.serpContents[0].hasEvaluation}
          />
        </Spacing>
      )}

      {locality && (
        <Spacing margin={[SpacingName.MEDIUM, 0]}>
          <RapidLinks
            aria-label="rapid-links"
            locality={locality}
            showAgency={hasSerpContent(serpData) && serpData.serpContents[0].hasAgency}
            showEvaluation={hasSerpContent(serpData) && serpData.serpContents[0].hasEvaluation}
            fallbackEvaluation
          />
        </Spacing>
      )}
      <PageLayout.Wrapper>
        <PageLayout.Breadcrumb items={breadcrumb} />
      </PageLayout.Wrapper>
      {isGeoSearch && (
        <StickyBar
          visible={!isSaveButtonVisible}
          hiddenFrom={BreakpointQueryName.LG}
        >
          <CreateWebSearchCriteriaButton
            dataRef="save-search-bottom-sticky"
            aria-label="save-search-bottom-sticky"
            dataTracking={`save-search:${DataTrackingContext.BOTTOM_STICKY}`}
            onSuccess={onSaveSearchCriteria}
            initialValues={initialWebSearchCriteriaWithContact}
            disabled={searchAlreadySaved}
            expanded
          />
        </StickyBar>
      )}
      <Portal>
        <Modal
          data-ref="save-search-modal-popup"
          open={saveSearchModal.isOpen}
          onCloseHandler={saveSearchModal.close}
        >
          <Modal.Close />

          <Modal.Header>
            <Typography.H3>
              Salva la tua ricerca
            </Typography.H3>
          </Modal.Header>

          <Modal.Body padded>
            <CreateWebSearchCriteria
              initialValues={initialWebSearchCriteriaWithContact}
              onSuccess={closePopupOnSaveSearchCriteria}
            />
          </Modal.Body>

        </Modal>
      </Portal>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<SerpPageProps>(async ({ query }) => {
  const { serverRuntimeConfig } = getConfig();
  const graphQLClient = initGraphQLClient();
  const page = query.p ? Number.parseInt(query.p as string, 10) : 1;
  const firstPageUrl = buildSerpURLObject({ ...query, p: '1' });

  if (Number.isNaN(page) || page < 1) {
    return {
      redirect: {
        destination: firstPageUrl.as,
        permanent: true,
      },
    };
  }

  let searchResults: SearchResults = null;
  let serpData = null;
  let queryFilters: PropertyFilters;

  if (query.ids) {
    const token = query.ids as string;
    let data;

    try {
      data = jwt.verify(token, serverRuntimeConfig.TROISI_JWT_SECRET);
    } catch (err) {
      logger.error({ err }, 'JWT not valid');
      throw new httpErrors.BadRequest('data JWT not valid');
    }

    queryFilters = { id: data.propertyIds as number[] };
  } else {
    queryFilters = queryToFilters(query as BuildSerpURLObject);
  }

  try {
    const promises = [
      getSearchResults({
        ...queryFilters,
        soldAt: {
          min: getMinSoldAtDate().toISOString(),
        },
      }, {
        page: page - 1,
        size: 15,
        sort: {
          promoted: SortOrder.DESC,
          _score: SortOrder.DESC,
          price: SortOrder.DESC,
          createdAt: SortOrder.DESC,
        },
      }),
    ];

    let graphQLParams: GraphQLRequest<Variables>;
    if (query.province) {
      graphQLParams = {
        query: SERP_CONTENTS_BY_PROVINCE_SLUG,
        variables: {
          slug: query.province,
        },
      };
    }

    if (query.locality && query.area) {
      graphQLParams = {
        query: SERP_CONTENTS_BY_LOCALITY_AND_AREA_SLUG,
        variables: {
          localitySlug: query.locality,
          areaSlug: query.area || null,
        },
      };
    }

    if (query.locality && !query.area) {
      graphQLParams = {
        query: SERP_CONTENTS_BY_LOCALITY_SLUG,
        variables: {
          slug: query.locality,
        },
      };
    }

    if (query.province || query.locality) {
      promises.push(
        graphQLClient.query(graphQLParams)
          .then((result) => result.data)
          .catch(() => null),
      );
    }

    [searchResults, serpData] = await Promise.all(promises);
  } catch (err) {
    throw new httpErrors.InternalServerError();
  }

  const { accurate } = searchResults;
  const { filters } = accurate.info;

  const province: PropertyFiltersLocation = objectPath.get(filters, 'province.0.value') ?? null;
  if (query.province && query.province !== province?.slug) {
    return {
      redirect: {
        destination: buildSerpURLObject({ ...query, province: province?.slug }).as,
        permanent: false,
      },
    };
  }

  const locality: PropertyFiltersLocation = objectPath.get(filters, 'locality.0.value') ?? null;
  if (query.locality && query.locality !== locality?.slug) {
    return {
      redirect: {
        destination: buildSerpURLObject({ ...query, locality: locality?.slug }).as,
        permanent: false,
      },
    };
  }

  const area: PropertyFiltersLocation = objectPath.get(filters, 'area.0.value') ?? null;
  if (locality && query.area && query.area !== area?.slug) {
    // Redirect to serp with given locality
    return {
      redirect: {
        destination: buildSerpURLObject({ ...query, area: area?.slug, locality: locality.slug }).as,
        permanent: false,
      },
    };
  }

  if (accurate.content.length === 0 && page > 1) {
    return {
      redirect: {
        destination: firstPageUrl.as,
        permanent: false,
      },
    };
  }

  let areaLinks: SeoLink[] = [];
  if (locality && ENABLED_LOCALITIES_TOP_AREAS.includes(locality.slug) && !area) {
    try {
      const propertiesCountsByArea = await getAggregationsByArea(locality.slug, getMinSoldAtDate().toISOString());
      areaLinks = propertiesAreaToLinksList(propertiesCountsByArea.area, locality.name);
    } catch (err) {
      logger.error({ err });
    }
  }

  const [data, adv] = await Promise.all([
    importDynamicData('pages/serp.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
  ]);

  const SEO = buildPropertySerpSEO(data.SEO, {
    province,
    locality,
    area,
    pageable: {
      page,
      first: accurate.first,
      last: accurate.last,
      hasResults: accurate.content.length > 0,
    },
  });

  return {
    props: {
      SEO,
      query: {
        ...query,
        province: query.province ?? null,
        locality: query.locality ?? null,
        area: query.area ?? null,
        p: query.p ?? null,
        size_min: query.size_min ?? null,
        size_max: query.size_max ?? null,
        price_min: query.price_min ?? null,
        price_max: query.price_max ?? null,
      } as BuildSerpURLObject,
      province,
      locality,
      area,
      adv,
      properties: accurate,
      results: {
        accurate: searchResults.accurate,
        nearby: searchResults.nearby ?? null,
        similar: searchResults.similar ?? null,
      },
      areaLinks,
      serpData: serpData ?? null,
      buyerConsultancy: data.buyerConsultancy ?? null,
    } as SerpPageProps,
  };
});

export default withError(SerpPage);
